import { formatDistanceToNow } from 'date-fns';
import {
  Bot,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  User,
  XCircle,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import type { SelectedCitation } from '@/components/agent-chat/context/AgentChatContext';
import { CitationComponent } from '@/components/agent-chat/messages/tiptap-extensions/Citations';
import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { FrontEmailModal } from '@/components/front-email-viewer';
import { cn } from '@/lib/utils';
import { useGetFrontConversationByDbIdQuery } from '@/services/api/frontMessageService';
import { AgentType } from '@/services/types/agent-types';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { DeliverableMatchStatus } from '@/services/types/deliverables-types';

export interface DeliverableMatchData {
  id: string;
  artifact_id?: string | null;
  artifact_type?: ArtifactType | null;
  artifact_name?: string | null;
  front_conversation_id?: string | null;
  status?: DeliverableMatchStatus | null;
  confidence?: number | null;
  context?: {
    rationale?: string | null;
    key_evidence?: string[] | null;
  } | null;
  agent?: AgentType | null;
  user_name?: string | null;
  created_at?: string;
  modified_at?: string;
}

export interface DeliverableMatchCardProps {
  /** The match data to display */
  match: DeliverableMatchData;
  /** Matter ID for API calls */
  matterId: string;
  /** Optional additional actions */
  additionalActions?: React.ReactNode;
  /** Whether to show the artifact inspection button */
  showInspectButton?: boolean;
  /** Optional handler for case-context style citation pills */
  setSelectedCitation?: (citation: SelectedCitation | null) => void;
  /** Presentation variant */
  variant?: 'default' | 'simple';
}

// Matches citation tags in any of three forms:
// 1. <cite artifact-id="A" citation-ids="page/1/block/2"/>
// 2. <cite citation-id="uuid"/>
// 3. <cite artifact-id="A"/> (artifact-id only, no citation-ids)
const CITATION_REGEX =
  /<cite\b[^>]*?(?:artifact-id=["']([^"']+)["'][^>]*?citation-ids=["']([^"']+)["']|citation-id=["']([^"']+)["']|artifact-id=["']([^"']+)["'])[\s\S]*?>/gi;

const parseEvidenceCitations = (
  text: string,
  matterId: string,
  setSelectedCitation?: (citation: SelectedCitation | null) => void,
) => {
  CITATION_REGEX.lastIndex = 0;
  const chips: React.ReactNode[] = [];
  if (setSelectedCitation) {
    let match: RegExpExecArray | null;
    let idx = 1;
    // eslint-disable-next-line no-cond-assign
    while ((match = CITATION_REGEX.exec(text)) !== null) {
      const citationIdOnly = match[3] || '';
      if (citationIdOnly) {
        chips.push(
          <CitationComponent
            key={`evidence-cite-${citationIdOnly}-${idx}`}
            citationId={citationIdOnly}
            matterId={matterId}
            setSelectedCitation={setSelectedCitation}
          />,
        );
        idx += 1;
      }

      // Legacy format: <cite artifact-id="A" citation-ids="page/1/block/2"/>
      // These are stripped from text but no longer rendered as badges
    }
  }

  const cleaned = text
    .replace(CITATION_REGEX, '')
    .replace(/<\/cite>/gi, '')
    .trim();
  return { cleaned, chips };
};

// Helper to get artifact type display info
const getArtifactTypeInfo = (artifactType?: ArtifactType | null) => {
  if (!artifactType)
    return { label: 'Document', color: 'bg-gray-100 text-gray-700' };

  const typeMap: Record<ArtifactType, { label: string; color: string }> = {
    [ArtifactType.DOCUMENT]: {
      label: 'Document',
      color: 'bg-blue-100 text-blue-700',
    },
    [ArtifactType.EMAIL]: {
      label: 'Email',
      color: 'bg-green-100 text-green-700',
    },
    [ArtifactType.EMAIL_ATTACHMENT]: {
      label: 'Email Attachment',
      color: 'bg-green-100 text-green-700',
    },
    [ArtifactType.TRANSCRIPT]: {
      label: 'Transcript',
      color: 'bg-purple-100 text-purple-700',
    },
    [ArtifactType.AUDIO_CONVERSATION]: {
      label: 'Audio',
      color: 'bg-purple-100 text-purple-700',
    },
    [ArtifactType.MESSAGE]: {
      label: 'Message',
      color: 'bg-yellow-100 text-yellow-700',
    },
    [ArtifactType.MESSAGE_ATTACHMENT]: {
      label: 'Message Attachment',
      color: 'bg-yellow-100 text-yellow-700',
    },
    [ArtifactType.INTAKE_NOTE]: {
      label: 'Intake Note',
      color: 'bg-indigo-100 text-indigo-700',
    },
    [ArtifactType.CONVERSATION]: {
      label: 'Conversation',
      color: 'bg-pink-100 text-pink-700',
    },
    [ArtifactType.BLOB]: { label: 'File', color: 'bg-gray-100 text-gray-700' },
    [ArtifactType.STAFF_NOTE]: {
      label: 'Staff Note',
      color: 'bg-gray-100 text-gray-700',
    },
  };

  return (
    typeMap[artifactType] || {
      label: artifactType,
      color: 'bg-gray-100 text-gray-700',
    }
  );
};

// Helper to get status display info
const getStatusInfo = (status?: DeliverableMatchStatus | null) => {
  switch (status) {
    case DeliverableMatchStatus.APPROVED:
      return {
        icon: <CheckCircle2 className="h-4 w-4" />,
        label: 'Approved',
        color: 'bg-green-100 text-green-700 border-green-200',
      };
    case DeliverableMatchStatus.REJECTED:
      return {
        icon: <XCircle className="h-4 w-4" />,
        label: 'Rejected',
        color: 'bg-red-100 text-red-700 border-red-200',
      };
    case DeliverableMatchStatus.NEEDS_REVIEW:
    default:
      return {
        icon: null,
        label: 'Needs Review',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
      };
  }
};

// Helper to format agent names
const formatAgentName = (agent?: AgentType | null): string => {
  if (!agent) return 'System';

  const agentNames: Record<AgentType, string> = {
    [AgentType.INGEST_TRANSCRIPT_TASK]: 'Transcript Processor',
    [AgentType.PROVIDER_FINDER]: 'Provider Finder',
    [AgentType.ARTIFACT_DIGEST]: 'Document Analyzer',
    [AgentType.BLAND_CALLER]: 'Phone Assistant',
    [AgentType.SLACK_TASK_BOT]: 'Slack Bot',
    [AgentType.BLOB_SORTER]: 'File Organizer',
    [AgentType.FRONT_APP_INTEGRATION]: 'Email Integration',
    [AgentType.TASK_GENERATOR]: 'Task Generator',
    [AgentType.COVERAGE_EXTRACTOR]: 'Coverage Analyzer',
    [AgentType.AIRTABLE_SYNC]: 'Airtable Sync',
    [AgentType.LOA_DETECTOR]: 'LOA Detector',
    [AgentType.SYSTEM]: 'System',
    [AgentType.RAINMAKER]: 'Rainmaker',
  };

  return agentNames[agent] || agent.replace(/_/g, ' ');
};

export const DeliverableMatchCard: React.FC<DeliverableMatchCardProps> = ({
  match,
  matterId,
  additionalActions,
  showInspectButton = true,
  setSelectedCitation,
  variant = 'default',
}) => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  // Use front_conversation_id (our database UUID) to fetch the conversation
  const conversationDbId = match.front_conversation_id || null;

  // Fetch the full conversation using our database UUID
  const { data: conversationData, isLoading: isLoadingConversation } =
    useGetFrontConversationByDbIdQuery(
      { matterId, conversationDbId: conversationDbId || '' },
      { skip: !conversationDbId },
    );

  const timeAgo = match.created_at
    ? formatDistanceToNow(new Date(match.created_at), { addSuffix: true })
    : null;

  const artifactTypeInfo = getArtifactTypeInfo(match.artifact_type);
  const statusInfo = getStatusInfo(match.status);
  const agentName = formatAgentName(match.agent);
  const senderLabel = match.user_name || agentName;
  const isSentByUser = Boolean(match.user_name);
  const recipientsFromEvidence = useMemo(() => {
    const evidenceLine =
      match.context?.key_evidence?.find((entry) =>
        entry?.toLowerCase?.().startsWith('recipients:'),
      ) ?? null;
    if (!evidenceLine) return null;
    const [, ...rest] = evidenceLine.split(':');
    const joined = rest.join(':').trim();
    return joined || null;
  }, [match.context?.key_evidence]);

  const outboundMessage = useMemo(() => {
    return conversationData?.messages?.find((message) => !message.is_inbound);
  }, [conversationData?.messages]);

  // For sent emails: "To" is the external party (conversation.recipient)
  // This is more reliable than message.recipients which can have different structures
  const recipientFromConversation = useMemo(() => {
    const recipient = conversationData?.conversation?.recipient;
    if (!recipient) return null;
    return recipient.name || recipient.handle || null;
  }, [conversationData?.conversation?.recipient]);

  const recipientDisplay =
    recipientFromConversation || recipientsFromEvidence || null;

  // For sent emails: "From" is the author of the outbound message (our team/firm)
  const emailSenderLabel = useMemo(() => {
    if (!outboundMessage?.author) return null;
    const { first_name, last_name, email } = outboundMessage.author;
    const fullName = `${first_name || ''} ${last_name || ''}`.trim();
    return fullName || email || null;
  }, [outboundMessage]);

  const hasContext =
    match.context?.rationale || (match.context?.key_evidence?.length ?? 0) > 0;

  const viewEmailButton = conversationDbId && (
    <button
      type="button"
      onClick={() => setIsEmailModalOpen(true)}
      disabled={isLoadingConversation}
      className={cn(
        'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
        variant === 'simple'
          ? 'text-blue-600 hover:bg-blue-50 disabled:text-zinc-400'
          : 'hover:text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed',
      )}
    >
      {isLoadingConversation ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading...
        </>
      ) : (
        <>
          <Mail className="h-3 w-3" />
          View Email
        </>
      )}
    </button>
  );

  const defaultContent = (
    <div className="border-l-4 border-l-blue-200 bg-blue-50/30 p-4 space-y-4 rounded-r-lg">
      {/* Header: Status, Type, and Actions */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status Badge */}
          <div
            className={cn(
              'flex items-center gap-1 text-xs px-2 py-1 rounded border font-medium',
              statusInfo.color,
            )}
          >
            {statusInfo.icon}
            {statusInfo.label}
          </div>

          {/* Artifact Type Badge */}
          <div
            className={cn(
              'text-xs px-2 py-1 rounded font-medium',
              artifactTypeInfo.color,
            )}
          >
            {artifactTypeInfo.label}
          </div>

          {/* Confidence Score */}
          {match.confidence !== null && match.confidence !== undefined && (
            <div
              className={cn(
                'text-xs px-2 py-1 rounded font-medium',
                (() => {
                  if (match.confidence > 0.8)
                    return 'bg-green-100 text-green-800';
                  if (match.confidence > 0.6)
                    return 'bg-yellow-100 text-yellow-800';
                  return 'bg-red-100 text-red-800';
                })(),
              )}
            >
              {(match.confidence * 100).toFixed(1)}% confidence
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {additionalActions}
          {viewEmailButton}
          {/* Show View Document button for artifact-based matches */}
          {showInspectButton &&
            match.artifact_id &&
            match.artifact_type &&
            !conversationDbId && (
              <InspectArtifactButton
                artifactId={match.artifact_id}
                artifactType={match.artifact_type}
                matterId={matterId}
                buttonClassName="flex items-center gap-1 hover:text-blue-600 transition-colors px-2 py-1 rounded hover:bg-blue-100 text-xs font-medium"
                icon={<ExternalLink className="h-3 w-3" />}
                label="View Document"
              />
            )}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex items-center gap-3 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          {isSentByUser ? (
            <User className="h-3 w-3" />
          ) : (
            <Bot className="h-3 w-3" />
          )}
          <span>{senderLabel}</span>
        </div>
        {timeAgo && (
          <>
            <span className="text-gray-400">•</span>
            <span>{timeAgo}</span>
          </>
        )}
      </div>

      {/* AI Context */}
      {hasContext && (
        <div className="space-y-3">
          {/* AI Rationale */}
          {match.context?.rationale && (
            <div className="text-sm">
              <div className="font-medium text-gray-700 mb-1">AI Analysis:</div>
              <div className="text-gray-600 bg-white/60 px-3 py-2 rounded border border-blue-200/50 leading-relaxed">
                {match.context.rationale}
              </div>
            </div>
          )}

          {/* Key Evidence */}
          {match.context?.key_evidence &&
            match.context.key_evidence.length > 0 && (
              <div className="text-sm">
                <div className="font-medium text-gray-700 mb-2">
                  Supporting Evidence ({match.context.key_evidence.length}):
                </div>
                <div className="space-y-2">
                  {match.context.key_evidence.map((evidence, index) => {
                    const { cleaned, chips } = parseEvidenceCitations(
                      evidence,
                      matterId,
                      setSelectedCitation,
                    );
                    return (
                      <div
                        key={index}
                        className="text-gray-700 bg-white/60 px-3 py-2 rounded border-l-4 border-l-blue-300 text-sm leading-relaxed"
                      >
                        <span>&ldquo;{cleaned}&rdquo;</span>
                        {chips.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1 text-xs">
                            {chips}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      )}

      {/* No Context State */}
      {!hasContext && (
        <div className="text-xs text-gray-500 italic">
          No additional context available for this match
        </div>
      )}
    </div>
  );

  // For simple variant (email cards), prefer actual email sender over match creator
  const simpleSenderLabel = emailSenderLabel || senderLabel;
  // Show Mail icon for emails, User/Bot for other matches
  const isEmailMatch = Boolean(conversationDbId);

  const simpleContent = (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-100">
            {isEmailMatch ? (
              <Mail className="h-4 w-4 text-zinc-500" />
            ) : isSentByUser ? (
              <User className="h-4 w-4 text-zinc-500" />
            ) : (
              <Bot className="h-4 w-4 text-zinc-500" />
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-zinc-900 truncate">
              {simpleSenderLabel}
            </span>
            {timeAgo && (
              <span className="text-xs text-zinc-500 truncate">{timeAgo}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 min-w-0 flex-1 px-4 border-l border-zinc-100">
          <span className="text-xs font-medium text-zinc-400 shrink-0">
            To:
          </span>
          <span
            className="text-sm text-zinc-700 truncate"
            title={recipientDisplay ?? ''}
          >
            {recipientDisplay ?? '—'}
          </span>
        </div>
      </div>

      <div className="shrink-0">{viewEmailButton}</div>
    </div>
  );

  return (
    <>
      {variant === 'simple' ? simpleContent : defaultContent}
      {/* Email Thread Modal */}
      {conversationData?.conversation?.id && (
        <FrontEmailModal
          isOpen={isEmailModalOpen}
          onOpenChange={setIsEmailModalOpen}
          matterId={matterId}
          conversationId={conversationData.conversation.id}
          conversationData={conversationData}
        />
      )}
    </>
  );
};
